/**
 * Utility functions for calculating cab prices dynamically based on distance and cab parameters
 * Includes discount calculation logic
 */

/**
 * Calculate the price for a cab based on distance and cab's pricing structure
 * @param {Object} cab - The cab object containing pricing information
 * @param {number} distance - The distance in kilometers
 * @returns {Object} - Object containing price details: {
 *   basePrice: number,      // Base price for included kilometers
 *   extraKmPrice: number,   // Price for extra kilometers (if any)
 *   totalPrice: number,     // Total calculated price
 *   includedKm: number,     // Included kilometers
 *   extraKm: number,        // Extra kilometers beyond included
 *   baseKmPrice: number,    // Price per km for included kilometers
 *   extraKmRate: number     // Price per km for extra kilometers
 * }
 */
export const calculateCabPrice = (cab, distance) => {
  if (!distance || !cab) {
    return {
      basePrice: 0,
      extraKmPrice: 0,
      totalPrice: 0,
      includedKm: 0,
      extraKm: 0,
      baseKmPrice: 0,
      extraKmRate: 0
    };
  }

  // Get cab pricing details with defaults
  const baseKmPrice = Number(cab.baseKmPrice) || 11.88;
  const includedKm = Number(cab.includedKm) || 0;
  const extraKmRate = Number(cab.extraFarePerKm) || (baseKmPrice * 1.2);

  // SIMPLIFIED CALCULATION: Base Price × Distance (km)
  // The total price is simply the base rate multiplied by the total distance
  const totalPrice = baseKmPrice * distance;

  // For backward compatibility, we'll still return the extra km values
  // but the calculation no longer uses them
  const extraKm = Math.max(0, distance - includedKm);
  const basePrice = totalPrice; // The entire amount is considered as base price
  const extraKmPrice = 0; // No separate extra km charges


  return {
    basePrice: Math.round(basePrice),
    extraKmPrice: Math.round(extraKmPrice),
    totalPrice: Math.round(totalPrice),
    includedKm,
    extraKm,
    baseKmPrice,
    extraKmRate
  };
};

/**
 * Calculate taxes and fees for a booking
 * @param {number} baseAmount - The base amount for the booking
 * @param {number} taxRate - The tax rate (default: 0.18 or 18% GST)
 * @returns {number} - The calculated tax amount (rounded to nearest integer)
 */
export const calculateTaxesAndFees = (baseAmount, taxRate = 0.18) => {
  // Ensure we're working with numbers, not strings
  const amount = Number(baseAmount) || 0;
  
  // Calculate taxes as percentage of base amount
  const taxes = amount * taxRate;
  
  // Ensure we have a reasonable minimum tax amount for larger fares
  if (amount > 10000 && taxes < 1800) {
    return Math.round(amount * 0.18); // Minimum 18% GST for large amounts
  }
  
  // For very low calculated taxes with non-zero base amounts, set minimum
  if (amount > 0 && taxes < 1) {
    return Math.max(1, Math.round(taxes)); // Minimum ₹1 tax
  }
  
  return Math.round(taxes);
};

/**
 * Calculate the total amount for a booking including taxes and fees
 * @param {number} basePrice - The base price for the booking
 * @param {number} taxes - The taxes for the booking
 * @param {number} tollCharges - The toll charges for the booking
 * @param {number} driverAllowance - The driver allowance for the booking
 * @param {number} nightCharges - The night charges for the booking
 * @param {number} distance - The distance for the booking (default: 0)
 * @returns {number} - The total amount including taxes (rounded to nearest integer)
 */
export const calculateTotalAmount = (basePrice, taxes, tollCharges, driverAllowance, nightCharges, distance = 0) => {
  // Convert all inputs to numbers and default to 0 if invalid
  const base = Number(basePrice) || 0;
  const tax = Number(taxes) || 0;
  const toll = Number(tollCharges) || 0;
  const driver = Number(driverAllowance) || 0;
  const night = Number(nightCharges) || 0;
  const dist = Number(distance) || 0;
  
  // Calculate total
  let total = base + tax + toll + driver + night;
  
  // Validate that the total is realistic for the distance
  if (dist > 100) {
    // For long-distance trips, ensure a minimum per-km fare (standard cab rate)
    const minPerKmRate = 12;
    const minExpectedTotal = dist * minPerKmRate;
    
    // If calculated total is unrealistically low (allowing for 10% flexibility)
    if (total < minExpectedTotal * 0.9) {
      total = minExpectedTotal;
    }
  }
  
  // Ensure we have a valid positive number that's rounded to the nearest integer
  return Math.round(Math.max(total, 0));
};

/**
 * Calculate discounted price based on original price and discount percentage
 * @param {number} originalPrice - The original price before discount
 * @param {number} discountPercentage - The discount percentage (e.g., 10 for 10%)
 * @returns {Object} - Object containing originalPrice, discountedPrice, and savingsAmount
 */
export const calculateDiscount = (originalPrice, discountPercentage = 10) => {
  // Ensure parameters are valid numbers
  const price = Number(originalPrice) || 0;
  const discount = Number(discountPercentage) || 0;
  
  // Calculate discounted price
  const savingsAmount = Math.round((price * discount) / 100);
  const discountedPrice = price - savingsAmount;
  
  return {
    originalPrice: price,
    discountedPrice,
    savingsAmount,
    discountPercentage: discount
  };
};
